import { SB } from '@play-co/replicant';
import { getLocaleConfig } from 'src/replicant/creatives/text';
import { getPetAssetKey, speciesChatbot } from './species';
import {
  createFlexContainer,
  createFlexMessageTemplate,
  createOpenGameAction,
} from './utils';
import { buildSender } from '../sender';

/**
 * Assets for reengagement messages
 */
export const reengagementAssets = {
  'background/reengagement/d7/en':
    'private/chatbot/reengagement/reengagement_7d_en.png',
  'background/reengagement/d7/ja':
    'private/chatbot/reengagement/reengagement_7d_ja.png',
  'background/reengagement/d14/en':
    'private/chatbot/reengagement/reengagement_14d_en.png',
  'background/reengagement/d14/ja':
    'private/chatbot/reengagement/reengagement_14d_ja.png',
  'background/reengagement/d28/en':
    'private/chatbot/reengagement/reengagement_28d_en.png',
  'background/reengagement/d28/ja':
    'private/chatbot/reengagement/reengagement_28d_ja.png',
};

/**
 * Create reengagement message
 *
 * https://developers.line.biz/en/docs/messaging-api/flex-message-elements/
 * https://developers.line.biz/en/docs/messaging-api/using-flex-messages/
 *
 * Tools for generation of messages in flex format:
 * https://developers.line.biz/flex-simulator/
 */
export const reengagementMessage = createFlexMessageTemplate(
  SB.object({
    day: SB.tuple(['7', '14', '28']),
    locale: SB.tuple(['en', 'ja']),
    petName: SB.string(),
    species: SB.tuple(speciesChatbot),
  }),
  ({ day, locale, petName, species }, payload, api) => {
    const dayIndex: 'd7' | 'd14' | 'd28' = `d${day}` as 'd7' | 'd14' | 'd28';
    const reengagement = getLocaleConfig(locale).reengagement[dayIndex];
    const text = reengagement.text;

    const icon = api.getAssetPath(getPetAssetKey(species));
    const background = api.getAssetPath(
      `background/reengagement/d${day}/${locale}`,
    );

    const template = createFlexContainer(text, {
      type: 'bubble',
      size: 'giga',
      action: createOpenGameAction(text, payload),
      body: {
        type: 'box',
        layout: 'vertical',
        paddingAll: '0px',
        contents: [
          {
            type: 'image',
            url: background,
            size: 'full',
            aspectMode: 'cover',
            aspectRatio: '1.5:1',
            gravity: 'center',
          },
        ],
      },
    });

    // Add sender to message
    template['sender'] = buildSender({ name: petName, iconUrl: icon });

    return template;
  },
);

export const reengagementVideoMessage = createFlexMessageTemplate(
  SB.object({
    day: SB.tuple(['7']),
    species: SB.tuple(speciesChatbot),
    locale: SB.tuple(['en', 'ja'] as const),
    petName: SB.string(),
  }),
  (data, payload, api) => {
    // Use the same alt text as resurrect message
    const altText = getLocaleConfig(data.locale).resurrect.altAll.text;
    const variant = '2025-02-22-video_save_pet_big';
    const species = data.species ?? 'ball';
    const normal = api.getAssetPath(getPetAssetKey(species, 'normal'));
    const videoUrl = api.getAssetPath(variant);
    const previewUrl = api.getAssetPath(variant + '_preview');
    const size = 'giga';

    const template = createFlexContainer(altText, {
      type: 'bubble',
      size,
      action: createOpenGameAction(altText, payload),
      hero: {
        type: 'video',
        url: videoUrl,
        previewUrl,
        altContent: {
          type: 'image',
          size: 'full',
          aspectRatio: '76:135',
          aspectMode: 'cover',
          url: previewUrl,
        },
        aspectRatio: '76:135',
        action: createOpenGameAction(altText, payload),
      },
      footer: {
        type: 'box',
        layout: 'vertical',
        spacing: 'sm',
        contents: [
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'text',
                text: altText,
                size: 'xxl',
                align: 'center',
                weight: 'bold',
                color: '#FFFFFF',
              },
            ],
            cornerRadius: 'md',
            height: '60px',
            justifyContent: 'center',
            background: {
              type: 'linearGradient',
              angle: '0deg',
              startColor: '#28a745',
              endColor: '#19c951',
            },
          },
        ],
      },
    });

    // Add sender to message
    template['sender'] = buildSender({ name: data.petName, iconUrl: normal });

    return template;
  },
);
